<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import { formatMoney, rowValueCents } from '../composables/commissionSheetForm';
import RealEstateCommissionBeneficiaryIdentity from './RealEstateCommissionBeneficiaryIdentity.vue';

const props = defineProps({
  amountCents: { type: Number, default: 0 },
  group: { type: String, required: true },
  options: { type: Array, default: () => [] },
  readonly: { type: Boolean, default: false },
  rows: { type: Array, default: () => [] },
  single: { type: Boolean, default: false },
  title: { type: String, required: true },
});

const emit = defineEmits(['add', 'payment', 'remove', 'select', 'update']);
const { t } = useI18n();
const canAdd = computed(() => !props.readonly && !props.single);
const companyOnly = computed(() =>
  ['empresas', 'repasse_franquias'].includes(props.group)
);
const optionLabel = option =>
  option.label || option.name || option.nome_empresarial;
const rowValue = row => formatMoney(rowValueCents(row, props.amountCents));
const paymentLabel = row =>
  row.tipo_pagamento === 'banco'
    ? row.nome_banco || t('REAL_ESTATE_COMMISSION_INSTALLMENT.PAYMENT.BANK')
    : row.chave_pix || t('REAL_ESTATE_COMMISSION_INSTALLMENT.PAYMENT.PIX');
</script>

<template>
  <LaisCard :title="title" prefix-icon="i-lucide-users-round">
    <template #titleAction>
      <LaisPrimaryButton
        v-if="canAdd"
        size="sm"
        variant="ghost"
        icon="i-lucide-plus"
        @click="emit('add')"
      >
        {{ t('REAL_ESTATE_COMMISSION_INSTALLMENT.ACTIONS.ADD') }}
      </LaisPrimaryButton>
    </template>
    <p v-if="!rows.length" class="text-sm text-lais-text-muted">
      {{ t('REAL_ESTATE_COMMISSION_INSTALLMENT.EDITOR.EMPTY_GROUP') }}
    </p>
    <div v-else class="grid gap-4">
      <article
        v-for="(row, index) in rows"
        :key="`${title}-${index}`"
        class="rounded-lais border border-lais-border-soft bg-lais-bg-soft p-4"
      >
        <div class="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          <LaisField
            v-if="!single"
            :label="t('REAL_ESTATE_COMMISSION_INSTALLMENT.FIELDS.RECIPIENT')"
          >
            <select
              :value="row.usuario_id || row.empresa_id || ''"
              :disabled="readonly"
              class="min-h-10 rounded-lais border border-lais-border bg-white px-3 text-sm disabled:cursor-not-allowed disabled:opacity-70"
              @change="emit('select', index, $event.target.value)"
            >
              <option value="">
                {{ t('REAL_ESTATE_COMMISSION_INSTALLMENT.FIELDS.SELECT') }}
              </option>
              <option
                v-for="option in options"
                :key="option.id"
                :value="option.id"
              >
                {{ optionLabel(option) }}
              </option>
            </select>
          </LaisField>
          <LaisField
            :label="t('REAL_ESTATE_COMMISSION_INSTALLMENT.FIELDS.PERCENTAGE')"
          >
            <input
              :value="row.porcentagem_real"
              :disabled="readonly"
              inputmode="decimal"
              min="0"
              max="100"
              class="min-h-10 rounded-lais border border-lais-border bg-white px-3 text-sm disabled:cursor-not-allowed disabled:opacity-70"
              @input="
                emit('update', index, 'porcentagem_real', $event.target.value)
              "
            />
          </LaisField>
          <LaisField
            v-if="!single"
            :label="t('REAL_ESTATE_COMMISSION_INSTALLMENT.FIELDS.DISCOUNT')"
          >
            <input
              :value="row.desconto"
              :disabled="readonly"
              inputmode="decimal"
              class="min-h-10 rounded-lais border border-lais-border bg-white px-3 text-sm disabled:cursor-not-allowed disabled:opacity-70"
              @input="emit('update', index, 'desconto', $event.target.value)"
            />
          </LaisField>
          <LaisField
            v-if="group === 'empresas'"
            :label="t('REAL_ESTATE_COMMISSION_INSTALLMENT.FIELDS.BONUS')"
          >
            <input
              :value="row.bonificacao"
              :disabled="readonly"
              inputmode="decimal"
              class="min-h-10 rounded-lais border border-lais-border bg-white px-3 text-sm disabled:cursor-not-allowed disabled:opacity-70"
              @input="emit('update', index, 'bonificacao', $event.target.value)"
            />
          </LaisField>
        </div>
        <RealEstateCommissionBeneficiaryIdentity
          v-if="!single"
          :company-only="companyOnly"
          :readonly="readonly"
          :row="row"
          @update="(field, value) => emit('update', index, field, value)"
        />
        <div class="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p class="text-xs text-lais-text-muted">
              {{ t('REAL_ESTATE_COMMISSION_INSTALLMENT.FIELDS.RECEIVABLE') }}
            </p>
            <p class="mt-1 font-semibold text-lais-primary">
              {{ rowValue(row) }}
            </p>
          </div>
          <div class="flex flex-wrap gap-2">
            <LaisPrimaryButton
              size="sm"
              variant="ghost"
              icon="i-lucide-wallet-cards"
              :disabled="readonly"
              @click="emit('payment', index)"
            >
              {{ paymentLabel(row) }}
            </LaisPrimaryButton>
            <LaisPrimaryButton
              v-if="!readonly && !single"
              size="sm"
              variant="ghost"
              icon="i-lucide-trash-2"
              @click="emit('remove', index)"
            >
              {{ t('REAL_ESTATE_COMMISSION_INSTALLMENT.ACTIONS.REMOVE') }}
            </LaisPrimaryButton>
          </div>
        </div>
      </article>
    </div>
  </LaisCard>
</template>
